/* Index ticker strip.
 *
 * The band every Indian trading screen carries: NIFTY, SENSEX, INDIA VIX.
 * Level, absolute change and percentage, always visible.
 *
 * The one thing this does that a decorative ticker would not: it distinguishes
 * a level that came from a market feed from one the SIMULATOR produced. Those
 * look identical as numbers and mean entirely different things, and a strip
 * showing "SENSEX 49.63" in the same typeface as a real quote is worse than a
 * strip showing nothing.
 */
import { useEffect, useState } from 'react'
import { api } from '@/lib/api'
import { fmtPrice, isSimulatedSource } from '@/lib/format'
import type { IndexQuote } from '@/types'

const REFRESH_MS = 30_000

interface Tracked extends IndexQuote {
  /** Previous level, so a change can be shown without a second endpoint. */
  prev?: number
}

export function IndexStrip() {
  const [items, setItems] = useState<Tracked[]>([])
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    let timer: number | undefined

    const load = async () => {
      try {
        const r = await api.indices()
        if (cancelled) return
        setItems((prev) => r.items.map((next) => {
          const before = prev.find((p) => p.instrument_id === next.instrument_id)
          return {
            ...next,
            // Carry the previous level forward so the strip can show movement.
            // Only meaningful once two samples exist; until then no change is
            // displayed rather than a fabricated zero.
            prev: before?.last != null ? Number(before.last) : before?.prev,
          }
        }))
        setFailed(false)
      } catch {
        if (!cancelled) setFailed(true)
      }
      if (!cancelled) timer = window.setTimeout(() => void load(), REFRESH_MS)
    }
    void load()
    return () => { cancelled = true; if (timer) window.clearTimeout(timer) }
  }, [])

  if (failed && items.length === 0) {
    return (
      <div style={{ ...strip, color: 'var(--text-muted)', fontSize: 'var(--fs-2xs)' }}>
        Index data is unavailable.
      </div>
    )
  }

  return (
    <div style={strip}>
      {items.map((ix) => {
        const simulated = ix.quoted && isSimulatedSource(ix.source)
        const last = ix.last != null ? Number(ix.last) : null
        const change = last != null && ix.prev != null ? last - ix.prev : null
        const changePct = change != null && ix.prev ? (change / ix.prev) * 100 : null
        const tone = change == null ? 'var(--text-muted)'
                   : change >= 0 ? 'var(--profit)' : 'var(--loss)'

        return (
          <div key={ix.instrument_id} style={cell} title={
            !ix.quoted ? `${ix.symbol}: no live feed is configured`
            : simulated ? `${ix.symbol}: this level came from the SIMULATOR (${ix.source}), not a market feed`
            : `${ix.symbol}: ${ix.source}`
          }>
            <span style={{
              fontSize: 'var(--fs-2xs)', fontWeight: 700, letterSpacing: '0.05em',
              color: 'var(--text-secondary)',
            }}>
              {ix.symbol}
            </span>

            <span className="num" style={{
              fontSize: 'var(--fs-sm)', fontWeight: 700,
              color: !ix.quoted ? 'var(--text-muted)'
                   : simulated ? 'var(--warn)' : 'var(--text-primary)',
            }}>
              {ix.quoted ? fmtPrice(last) : '—'}
            </span>

            {change != null && changePct != null ? (
              <span className="num" style={{ fontSize: 9, color: tone }}>
                {change >= 0 ? '+' : ''}{change.toFixed(2)}
                {' '}({changePct >= 0 ? '+' : ''}{changePct.toFixed(2)}%)
              </span>
            ) : (
              <span style={{ fontSize: 9, color: 'var(--text-muted)' }}>
                {/* Not "0.00%". No second sample yet means no change is known,
                    and zero would read as a flat market. */}
                {!ix.quoted ? 'no feed' : simulated ? 'SIMULATED' : 'awaiting move'}
              </span>
            )}
          </div>
        )
      })}
    </div>
  )
}

const strip: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 'var(--sp-5)',
  padding: '0 var(--sp-3)', overflowX: 'auto',
  background: 'var(--bg-panel)', border: '1px solid var(--border)',
  borderRadius: 'var(--radius-md)',
}

const cell: React.CSSProperties = {
  display: 'flex', alignItems: 'baseline', gap: 'var(--sp-2)',
  whiteSpace: 'nowrap', flexShrink: 0,
}
